import type { FC } from 'react';
import React from 'react';
import { cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { usePageContentId } from '@confluence/page-context';
import { getEmojiFromPublishedPageProperties } from '@confluence/emoji-title/entry-points/transformer.ts';
import {
	SpaceOverviewQuery,
	type SpaceOverviewQuery$data,
	type SpaceOverviewQuery$variables,
} from '@confluence/space-overview-query/entry-points/SpaceOverviewQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { SmartSpaceOverviewCoverPicture } from './SmartSpaceOverviewCoverPicture.tsx';
import { SmartSpaceOverviewSummary } from './SmartSpaceOverviewSummary.tsx';

const styles = cssMap({
	container: {
		backgroundColor: token('elevation.surface'),
		padding: token('space.600'),
		maxWidth: '1200px',
		margin: '0 auto',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.400'),
	},
});

type SmartSpaceOverviewProps = {
	spaceKey: string;
};

export const SmartSpaceOverview: FC<SmartSpaceOverviewProps> = ({ spaceKey }) => {
	const [contentId] = usePageContentId();

	const spaceOverviewQueryResult = useQueryInterop<
		SpaceOverviewQuery$data,
		SpaceOverviewQuery$variables
	>(SpaceOverviewQuery, {
		variables: {
			spaceKey,
			excludeOperations: true,
		},
	});

	const spaceData = spaceOverviewQueryResult.data;
	const isAdminLoading = spaceOverviewQueryResult.loading;
	const isAdmin = spaceData?.space?.currentUser?.isAdmin ?? false;
	const emoji = getEmojiFromPublishedPageProperties(spaceData?.space?.homepage ?? null);
	const spaceIconPath = spaceData?.space?.icon?.path;
	const hasEmoji = !!emoji || !!spaceIconPath;

	if (!contentId || !spaceKey || isAdminLoading) {
		return null;
	}

	return (
		<div css={styles.container}>
			<SmartSpaceOverviewCoverPicture contentId={contentId} isAdmin={isAdmin} hasEmoji={hasEmoji} />
			<SmartSpaceOverviewSummary spaceKey={spaceKey} />
		</div>
	);
};
